// money.js

// Always show exactly `precision` decimals
function formatMoney(value, opts) {
  let n = parseFloat(value);
  if (isNaN(n)) n = 0;
  const { precision, decimal, thousands, prefix, suffix } = opts;

  // toFixed pads out trailing zeros:
  const fixedStr = n.toFixed(precision);    // e.g. "1234.50" or "2.10"
  const [whole, decPart] = fixedStr.split('.');

  // thousands separator on the integer part:
  const withTh = thousands
    ? whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands)
    : whole;

  return prefix
    + withTh
    + (precision > 0 ? decimal + decPart : '')
    + suffix;
}

export default {
  name: 'money',

  bind(el, binding, vnode) {
    const input = el.querySelector('input');
    if (!input) return;

    // 1) Merge defaults + user options, compute factor (10^precision)
    const opts = {
      decimal:  '.', 
      thousands:',', 
      prefix:   '', 
      suffix:   '', 
      precision: 2
    };
    // const opts   = el._opts = { ...defaults, ...binding.value };
    const factor = Math.pow(10, opts.precision);

    // 2) Initialize raw (an integer count of cents) from the v-model
    const initVal = parseFloat(vnode.data.model?.value) || 0;
    el._raw = Math.round(initVal * factor);

    // 3) Show initial formatted value
    input.value = formatMoney(el._raw / factor, opts);

    const updateDisplay = () => {
      let display = formatMoney(el._raw / factor, opts);
      // console.log("updateDisplay", el._raw, display);
      input.value = display;
      
      // Update the v-model value
      const actualValue = el._raw / factor;
      
      // For Vuetify components, emit input event
      if (vnode.componentInstance) {
        vnode.componentInstance.$emit('input', actualValue);
        // Also trigger change event for better Vuetify integration
        vnode.componentInstance.$emit('change', actualValue);
      } 
      // For regular elements with v-model, call the callback
      else if (vnode.data && vnode.data.model && vnode.data.model.callback) {
        vnode.data.model.callback(actualValue);
      }
      // Try to update through the context if available
      else if (vnode.context && vnode.data.model) {
        const expression = vnode.data.model.expression;
        if (expression && vnode.context) {
          // Use Vue's $set to update the value reactively
          const path = expression.split('.');
          let obj = vnode.context;
          for (let i = 0; i < path.length - 1; i++) {
            if (obj && obj[path[i]] !== undefined) {
              obj = obj[path[i]];
            } else {
              return; // Path doesn't exist, exit early
            }
          }
          if (obj && typeof obj === 'object') {
            vnode.context.$set(obj, path[path.length - 1], actualValue);
            // Force Vue to detect the change
            vnode.context.$forceUpdate();
          }
        }
      }
    };

    // 4) Intercept keydown for digits & Backspace
    const onKeydown = e => {
      // updateFromRaw();
      if (e.key >= '0' && e.key <= '9') {
        e.preventDefault();
        el._raw = el._raw * 10 + Number(e.key);
        // console.log("keydown", e.key, el._raw);
        updateDisplay();
      }
      else if (e.key === 'Backspace') {
        e.preventDefault();
        el._raw = Math.floor(el._raw / 10);
        // console.log("backspace", e.key, el._raw);
        updateDisplay();
      }
      else {
        e.preventDefault();
      }
    };

    // 5) Intercept paste (grab only digits)
    const onPaste = e => {
      const text   = e.clipboardData.getData('text') || '';
      const digits = text.replace(/[^0-9]/g, '');
      el._raw = digits ? parseInt(digits, 10) : 0;
    };

    // 6) On blur: always redraw
    const onBlur = () => {
      updateDisplay();
    };

    // 7) On focus: always redraw
    const onFocus = () => {
      updateDisplay();
    };

    // Attach & save for unbind
    el._kd = onKeydown;
    el._pt = onPaste;
    el._bl = onBlur;
    el._fc = onFocus;
    input.addEventListener('keydown', onKeydown);
    input.addEventListener('paste',   onPaste);
    input.addEventListener('blur',    onBlur);
    input.addEventListener('focus',   onFocus);
  },

  update(el, binding, vnode) {
    // Handle external changes to the model value (like from watchers or API calls)
    const input = el.querySelector('input');
    if (!input) return;

    const factor = 100; // precision 2
    const newVal = parseFloat(vnode.data.model?.value) || 0;
    
    // Only update if the value actually changed to avoid infinite loops
    if (Math.abs(el._raw / factor - newVal) > 0.001) {
      el._raw = Math.round(newVal * factor);
      const opts = {
        decimal: '.', 
        thousands: ',', 
        prefix: '', 
        suffix: '', 
        precision: 2
      };
      input.value = formatMoney(el._raw / factor, opts);
    }
  },

  unbind(el) {
    const input = el.querySelector('input');
    if (!input) return;
    input.removeEventListener('keydown', el._kd);
    input.removeEventListener('paste',   el._pt);
    input.removeEventListener('blur',    el._bl);
    input.removeEventListener('focus',   el._fc);
    delete el._opts;
    delete el._raw;
    delete el._kd;
    delete el._pt;
    delete el._bl;
    delete el._fc;
  }
};
